import { useEffect, useRef, useState } from 'react';
import {
  AnalysisNotFoundError,
  getAnalysis,
  type AnalysisProgress,
  type AnalysisStatus,
} from '../api.js';
import { timestamp } from '../format.js';
import { progressSteps, type StepState } from '../run-progress.js';
import { AnalyzerLink } from './AnalyzerLink.js';

/**
 * A re-underwrite, FOLLOWED IN PLACE on the project page (#1396).
 *
 * 🔴 WHY THIS EXISTS. The panel used to `navigate('/analyses/:id')` the moment the server accepted the
 * run. The verb is Hub-only, and the Hub has no `/analyses` route — its catch-all redirected the reader
 * to `/projects`. So a confirmed, PAID run answered with "the screen flipped" to the project list, and
 * nothing anywhere said the run had started. Now the reader stays on the record and this notice
 * follows the run until it ends, then links to the result on the analyzer's origin. An upload that
 * starts a run is followed the same way (#1399) — it used to navigate, and bounce, identically.
 *
 * ⚠️ IT SHOWS ONLY WHAT THE SERVER REPORTS. `GET /analyses/:id` carries a lifecycle `status`
 * (`queued` → `running`, with `awaiting_index` as the ingestion waypoint), timestamps and — since
 * #1400 — `progress`: the last checkpoint the worker passed. When a stage is reported this draws the
 * ordered steps (done / now / to come) and how long the run has been on the current one. When none is
 * (an older run, a deal run, a server that predates the field, a stage this build does not know) it
 * falls back to the status line and says plainly it cannot say how far through the run is. Never a
 * percentage: the steps are nowhere near equal in length, so a bar would be a guess drawn as a
 * measurement.
 *
 * ⚠️ POLLED SLOWER THAN THE REPORT PAGE. `AnalysisView` polls every 2.5s because the reader is staring
 * at a spinner; here the reader is working on the record, and a multi-minute run does not need a
 * 2.5s answer. Each poll of that route also runs the currency/tier reads, so it is not free.
 */

/** How often the run is asked about. Exported so the tests advance exactly one interval. */
export const RUN_POLL_MS = 5_000;
/**
 * When this page stops asking. Not when the RUN stops — it says so and links to the run, which the
 * analyzer keeps following. Generous: a large set (hundreds of judged documents) runs for many minutes.
 */
export const RUN_POLL_CEILING_MS = 45 * 60_000;
/** Consecutive failed polls before giving up. One blip (a deploy, a dropped packet) must not end it. */
const MAX_CONSECUTIVE_ERRORS = 3;

const TERMINAL: ReadonlySet<AnalysisStatus> = new Set(['succeeded', 'failed', 'canceled']);

/** Why this page stopped following a run that had NOT ended. Each says so; none claims an outcome. */
type Stopped = 'session' | 'unreachable' | 'not_found' | 'ceiling';

/**
 * What started the run being followed — it decides only the WORDS. `reunderwrite`: this reader pressed
 * the verb. `joined`: the server refused a second run and named the one already going (`409
 * in_flight`), so the reader did not start it and must not be told they did. `upload`: documents this
 * reader added started it (#1399).
 */
export type RunTrigger = 'reunderwrite' | 'joined' | 'upload';

const NOUN: Record<RunTrigger, string> = {
  reunderwrite: 're-underwrite',
  joined: 'underwrite',
  upload: 'underwrite',
};

const RUNNING_HEADLINE: Record<RunTrigger, string> = {
  reunderwrite: 'Re-underwrite running',
  joined: 'An underwrite of this record was already running',
  upload: 'Underwrite of the added documents running',
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "45s", "4m 05s", "1h 02m" — elapsed wall time, never negative (a server clock ahead of ours). */
export function elapsedLabel(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${String(s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${String(m)}m ${String(s % 60).padStart(2, '0')}s`;
  return `${String(Math.floor(m / 60))}h ${String(m % 60).padStart(2, '0')}m`;
}

/** Each step's marker, and the word a screen reader hears for it (the glyph alone says nothing). */
const STEP_MARK: Record<StepState, { glyph: string; said: string }> = {
  done: { glyph: '✓', said: 'done' },
  current: { glyph: '▸', said: 'now' },
  upcoming: { glyph: '○', said: 'to come' },
};

/** The phase line — one sentence per status the server can actually report, and nothing finer. */
function phaseOf(status: AnalysisStatus | null): string {
  if (status === 'running') return 'Underwriting — the engine, then the model-judged sections.';
  if (status === 'awaiting_index') return 'Waiting for Nexus to finish indexing the documents.';
  if (status === 'queued') return 'Queued — waiting for a runner to pick it up.';
  // Before the first answer: say nothing the server has not said.
  return 'Checking on it…';
}

export function ReunderwriteRun({
  analysisId,
  trigger,
  submittedAt,
  onSettled,
}: {
  analysisId: string;
  trigger: RunTrigger;
  /** When the act happened (ISO). The start time shown until the server reports `createdAt`. */
  submittedAt: string;
  /**
   * Called once when this notice stops following the run: with the terminal status, or `null` when it
   * stopped WITHOUT one (session gone, server unreachable, ceiling). The panel re-offers the verb then.
   */
  onSettled: (status: AnalysisStatus | null) => void;
}) {
  const [status, setStatus] = useState<AnalysisStatus | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [progress, setProgress] = useState<AnalysisProgress | null>(null);
  const [stopped, setStopped] = useState<Stopped | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // A ref, so a parent re-render with a new callback neither restarts the poll nor is missed by it.
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  }, [onSettled]);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let errors = 0;
    const deadline = Date.now() + RUN_POLL_CEILING_MS;

    const stop = (why: Stopped) => {
      setStopped(why);
      onSettledRef.current(null);
    };

    async function tick() {
      try {
        const run = await getAnalysis(analysisId);
        if (!active) return;
        if (!run) {
          // 401 — the session ended. Not a failed run: the run is the server's, and it carries on.
          stop('session');
          return;
        }
        errors = 0;
        setStatus(run.status);
        setCreatedAt(run.createdAt);
        setCompletedAt(run.completedAt);
        // Absent (an older server) and null (no stage) are the same answer to the reader.
        setProgress(run.progress ?? null);
        if (TERMINAL.has(run.status)) {
          onSettledRef.current(run.status);
          return;
        }
      } catch (err) {
        if (!active) return;
        // A 404 is an ANSWER, not a blip: no such run is visible to this reader. Retrying will not
        // change it, and "lost contact" would be untrue.
        if (err instanceof AnalysisNotFoundError) {
          stop('not_found');
          return;
        }
        errors += 1;
        if (errors >= MAX_CONSECUTIVE_ERRORS) {
          stop('unreachable');
          return;
        }
      }
      if (Date.now() >= deadline) {
        stop('ceiling');
        return;
      }
      timer = setTimeout(() => void tick(), RUN_POLL_MS);
    }

    void tick();
    // ⚠️ THE CLEANUP IS THE CONTRACT. Leaving the tab or the page must stop the polling — `active`
    // drops any answer already in flight, and the pending timer never fires.
    return () => {
      active = false;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [analysisId]);

  const live = stopped === null && (status === null || !TERMINAL.has(status));

  // The elapsed clock ticks only while the run is live — a finished run's duration is fixed.
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, [live]);

  const startedAt = createdAt ?? submittedAt;
  const runPath = `/analyses/${encodeURIComponent(analysisId)}`;
  const noun = NOUN[trigger];

  if (status === 'succeeded') {
    return (
      <div className="notice docs__run" role="status">
        <strong>{cap(noun)} finished</strong> — {timestamp(completedAt)}.{' '}
        <AnalyzerLink className="docs__runlink" to={runPath}>
          Open the new underwrite →
        </AnalyzerLink>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="notice error docs__run" role="status">
        <strong>The {noun} didn’t finish.</strong> The record still shows the previous underwrite.
        You can start it again.{' '}
        <AnalyzerLink className="docs__runlink" to={runPath}>
          See the run →
        </AnalyzerLink>
      </div>
    );
  }
  if (status === 'canceled') {
    return (
      <div className="notice docs__run" role="status">
        <strong>The {noun} was canceled.</strong> The record still shows the previous underwrite.
      </div>
    );
  }
  if (stopped === 'not_found') {
    return (
      <div className="notice warn docs__run" role="status">
        The server has no run by that id that you can see, so this page stopped following it.
        Refresh the record to see its latest underwrite.
      </div>
    );
  }
  if (stopped !== null) {
    const why =
      stopped === 'session'
        ? 'Your session ended, so this page stopped following the run. Sign in again to see how it finished.'
        : stopped === 'unreachable'
          ? 'We lost contact with the server while following the run. It may still be running.'
          : `Still running after ${String(RUN_POLL_CEILING_MS / 60_000)} minutes — this page has stopped checking.`;
    return (
      <div className="notice warn docs__run" role="status">
        {why}{' '}
        <AnalyzerLink className="docs__runlink" to={runPath}>
          Open the run →
        </AnalyzerLink>
      </div>
    );
  }

  // `null` for no stage OR a stage this build cannot place — both fall back to the status line.
  const steps = progressSteps(progress?.stage);

  return (
    <div className="notice docs__run" role="status" aria-live="polite">
      <strong>{RUNNING_HEADLINE[trigger]}</strong> — started {timestamp(startedAt)}.{' '}
      <span className="docs__runelapsed">· {elapsedLabel(now - Date.parse(startedAt))}</span>
      {steps && progress ? (
        // Component-scoped spacing only: the shared stylesheet is not this change's to edit, and a
        // plain list needs nothing more than its indent removed.
        <ol
          className="docs__runsteps"
          aria-label="How far along the run is"
          style={{ listStyle: 'none', margin: '6px 0', padding: 0 }}
        >
          {steps.map((s) => (
            <li key={s.stage} aria-current={s.state === 'current' ? 'step' : undefined}>
              <span role="img" aria-label={STEP_MARK[s.state].said}>
                {STEP_MARK[s.state].glyph}
              </span>{' '}
              {s.state === 'current' ? <strong>{s.label}</strong> : s.label}
              {s.state === 'current' && (
                <span className="docs__runelapsed">
                  {' '}
                  · {elapsedLabel(now - Date.parse(progress.at))} on this step
                </span>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <>
          <br />
          {phaseOf(status)}
          <br />
        </>
      )}
      <span className="card__note">
        You can stay on this page — this updates when the run finishes.
        {/* Said only when it is true: with a stage on screen, the server HAS reported how far along. */}
        {!steps &&
          ' The server reports whether a run is queued or underwriting, not how far through it is.'}
      </span>
    </div>
  );
}
